/**
 * SteinKunstWerk pre-publish SEO and conversion cleanup v2.
 * Scope: visible breadcrumbs and product-page cleanup only.
 * Structured-data breadcrumbs are rendered server-side in skw-seo-schema.liquid.
 */
(() => {
  'use strict';

  const PRODUCT_PATH = /^\/products\//;
  const COLLECTION_PATH = /^\/collections\/[^/]+/;
  const BLOG_PATH = /^\/blogs\/[^/]+/;
  const PAGE_PATH = /^\/pages\/[^/]+/;
  const LOCALE_PREFIX = /^\/(nl-be|fr-be|de|en|nl|fr|tr)(?=\/|$)/i;

  const language = () => (document.documentElement.lang || 'de').toLowerCase().split('-')[0];
  const localeRoot = () => {
    const match = window.location.pathname.match(LOCALE_PREFIX);
    return match ? `/${match[1].toLowerCase()}` : '';
  };
  const storefrontPath = () => window.location.pathname.replace(LOCALE_PREFIX, '') || '/';
  const localUrl = (path) => {
    const root = localeRoot();
    if (path === '/') return root ? `${root}/` : '/';
    return `${root}${path}`;
  };

  const COPY = {
    de: { home: 'Startseite', products: 'Produkte', collections: 'Kollektionen', blog: 'Blog', payment: 'Sichere Zahlungsabwicklung', paymentTail: 'im Shopify-Checkout', packed: 'Sorgfältig verpackt', packedTail: 'für einen geschützten Versand', policies: 'Klare Informationen', policiesTail: 'zu Versand, Widerruf und Rückgabe', paymentLine: 'Die verfügbaren Zahlungsarten werden im Checkout angezeigt.', shipping: 'Versandbedingungen und Kosten werden transparent ausgewiesen.', service: 'Bei Fragen hilft der SteinKunstWerk-Kundenservice weiter.' },
    en: { home: 'Home', products: 'Products', collections: 'Collections', blog: 'Blog', payment: 'Secure payment processing', paymentTail: 'in the Shopify checkout', packed: 'Carefully packed', packedTail: 'for protected shipping', policies: 'Clear information', policiesTail: 'on shipping, withdrawal and returns', paymentLine: 'Available payment methods are shown at checkout.', shipping: 'Shipping terms and costs are displayed transparently.', service: 'The SteinKunstWerk customer service team can help with questions.' },
    nl: { home: 'Startpagina', products: 'Producten', collections: 'Collecties', blog: 'Blog', payment: 'Veilige betalingsverwerking', paymentTail: 'in de Shopify-checkout', packed: 'Zorgvuldig verpakt', packedTail: 'voor beschermd vervoer', policies: 'Duidelijke informatie', policiesTail: 'over verzending, herroeping en retouren', paymentLine: 'Beschikbare betaalmethoden worden bij het afrekenen getoond.', shipping: 'Verzendvoorwaarden en kosten worden transparant weergegeven.', service: 'De klantenservice van SteinKunstWerk helpt bij vragen.' },
    fr: { home: 'Accueil', products: 'Produits', collections: 'Collections', blog: 'Blog', payment: 'Paiement sécurisé', paymentTail: 'dans le checkout Shopify', packed: 'Emballé avec soin', packedTail: 'pour un transport protégé', policies: 'Informations claires', policiesTail: 'sur la livraison, la rétractation et les retours', paymentLine: 'Les modes de paiement disponibles sont affichés lors du checkout.', shipping: 'Les conditions et frais de livraison sont indiqués de manière transparente.', service: 'Le service client SteinKunstWerk répond à vos questions.' },
    tr: { home: 'Ana sayfa', products: 'Ürünler', collections: 'Koleksiyonlar', blog: 'Blog', payment: 'Güvenli ödeme işlemi', paymentTail: 'Shopify ödeme ekranında', packed: 'Özenle paketlenir', packedTail: 'korumalı gönderim için', policies: 'Açık bilgiler', policiesTail: 'gönderim, cayma ve iade hakkında', paymentLine: 'Kullanılabilir ödeme yöntemleri ödeme ekranında gösterilir.', shipping: 'Gönderim koşulları ve ücretleri şeffaf şekilde gösterilir.', service: 'Sorularınız için SteinKunstWerk müşteri hizmetleri yardımcı olur.' },
  };
  const copy = () => COPY[language()] || COPY.de;

  try {
    localStorage.setItem('skwHide', '1');
  } catch (_) {
    // Storage can be unavailable in strict privacy modes.
  }

  document.documentElement.classList.add('skw-prepublish-cleanup');

  const ensureStyles = () => {
    if (document.getElementById('skw-prepublish-optimization-styles')) return;

    const style = document.createElement('style');
    style.id = 'skw-prepublish-optimization-styles';
    style.textContent = `
      .skw-prepublish-cleanup .skw-proof,
      .skw-prepublish-cleanup .skw-product-info,
      .skw-prepublish-cleanup .bl-scan-box{display:none!important}
      .skw-breadcrumbs{width:100%;max-width:var(--page-width,120rem);margin:0 auto;padding:12px 5rem 4px;font-size:1.3rem;line-height:1.45;color:rgba(var(--color-foreground),.68)}
      .skw-breadcrumbs ol{display:flex;flex-wrap:wrap;align-items:center;gap:6px;margin:0;padding:0;list-style:none}
      .skw-breadcrumbs li{display:inline-flex;align-items:center;min-width:0}
      .skw-breadcrumbs li:not(:last-child)::after{content:'›';margin-left:6px;opacity:.55}
      .skw-breadcrumbs a{color:inherit;text-decoration:none;text-underline-offset:3px}
      .skw-breadcrumbs a:hover,.skw-breadcrumbs a:focus-visible{color:rgb(var(--color-link));text-decoration:underline}
      .skw-breadcrumbs [aria-current='page']{color:rgba(var(--color-foreground),.9);font-weight:650;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:min(52rem,70vw)}
      @media(max-width:749px){.skw-breadcrumbs{padding:10px 1.5rem 2px;font-size:1.2rem}.skw-breadcrumbs [aria-current='page']{max-width:68vw}}
    `;
    document.head.appendChild(style);
  };

  const currentTitle = () => {
    const heading = document.querySelector('#MainContent h1, main h1, article h1, h1');
    return heading?.textContent?.replace(/\s+/g, ' ').trim() || document.title.split('–')[0].trim();
  };

  const buildBreadcrumbItems = () => {
    const path = storefrontPath().replace(/\/+$/, '') || '/';
    const title = currentTitle();
    const t = copy();
    const items = [{ name: t.home, url: localUrl('/') }];

    if (PRODUCT_PATH.test(path)) {
      items.push({ name: t.products, url: localUrl('/collections/all') }, { name: title, url: localUrl(path) });
    } else if (COLLECTION_PATH.test(path)) {
      items.push({ name: t.collections, url: localUrl('/collections') }, { name: title, url: localUrl(path) });
    } else if (BLOG_PATH.test(path)) {
      const parts = path.split('/').filter(Boolean);
      const blogUrl = localUrl(parts.length >= 2 ? `/blogs/${parts[1]}` : '/blogs');
      if (parts.length >= 3) {
        items.push({ name: t.blog, url: blogUrl }, { name: title, url: localUrl(path) });
      } else {
        items.push({ name: title || t.blog, url: localUrl(path) });
      }
    } else if (PAGE_PATH.test(path)) {
      items.push({ name: title, url: localUrl(path) });
    } else {
      return [];
    }

    return items.filter((item) => item.name);
  };

  const addBreadcrumbs = () => {
    if (document.querySelector('.skw-breadcrumbs,[aria-label="Breadcrumb"],[aria-label="Brotkrümelnavigation"]')) return;

    const main = document.getElementById('MainContent') || document.querySelector('main');
    if (!main) return;

    const items = buildBreadcrumbItems();
    if (items.length < 2) return;

    const nav = document.createElement('nav');
    nav.className = 'skw-breadcrumbs';
    nav.setAttribute('aria-label', 'Brotkrümelnavigation');

    const list = document.createElement('ol');
    items.forEach((item, index) => {
      const li = document.createElement('li');
      if (index === items.length - 1) {
        const span = document.createElement('span');
        span.textContent = item.name;
        span.setAttribute('aria-current', 'page');
        li.appendChild(span);
      } else {
        const link = document.createElement('a');
        link.href = item.url;
        link.textContent = item.name;
        li.appendChild(link);
      }
      list.appendChild(li);
    });
    nav.appendChild(list);
    main.insertAdjacentElement('afterbegin', nav);
  };

  const cleanProductPage = () => {
    if (!PRODUCT_PATH.test(storefrontPath())) return;

    const removeGenericBlocks = () => {
      document.querySelectorAll('.skw-proof,.skw-product-info,.bl-scan-box').forEach((element) => element.remove());
    };

    const normalizeTrustCopy = () => {
      const area = document.querySelector('[data-skw-buy]');
      if (!area || area.dataset.skwTrustNormalized === '1') return;
      const t = copy();

      const trust = area.querySelector('.skw-trust-list');
      if (trust) {
        trust.innerHTML = `
          <div class="skw-trust-item"><span class="skw-trust-check">✓</span><span><strong>${t.payment}</strong> ${t.paymentTail}</span></div>
          <div class="skw-trust-item"><span class="skw-trust-check">✓</span><span><strong>${t.packed}</strong> ${t.packedTail}</span></div>
          <div class="skw-trust-item"><span class="skw-trust-check">✓</span><span><strong>${t.policies}</strong> ${t.policiesTail}</span></div>
        `;
      }

      const payment = area.querySelector('.skw-payment-line');
      if (payment) payment.textContent = t.paymentLine;

      const info = area.querySelector('.skw-buy-info');
      if (info) {
        info.innerHTML = `
          <div class="skw-buy-info-row"><span class="skw-buy-info-dot">•</span><span>${t.shipping}</span></div>
          <div class="skw-buy-info-row"><span class="skw-buy-info-dot">•</span><span>${t.service}</span></div>
        `;
      }

      area.dataset.skwTrustNormalized = '1';
    };

    removeGenericBlocks();
    normalizeTrustCopy();

    const observer = new MutationObserver(() => {
      removeGenericBlocks();
      normalizeTrustCopy();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 6500);
  };

  const init = () => {
    ensureStyles();
    addBreadcrumbs();
    cleanProductPage();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
